import React, { useEffect, useRef, useState } from "react";
import { updateCampaign } from "../../../api/UpdateCampaign";
import GeneralButton from "../../shared/Buttons/GeneralButton";
import SharedTooltip from "../../shared/Tooltip/SharedTooltip";
import editClasses from "./EditCampaign.module.css";
import { errorHandler } from "../../shared/Utils/ErrorHandler";
import Editor from "../CampaignModal/Editor/Editor";
import { arrayMove } from "@dnd-kit/sortable";
import { DndContext, closestCenter, PointerSensor, useSensor, useSensors } from "@dnd-kit/core";
import { SortableContext, useSortable, horizontalListSortingStrategy } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import Compressor from "compressorjs";
import SecondaryButton from "../../shared/Buttons/SecondaryButton";
import CampaignShareBox from "./CampaignShareBox";
import { urlifyName } from "../../shared/Utils/PageUrl";
import CampaignUpdateForm from "./CampaignUpdateForm";

const ageOptions = {
  Newborn: 1,
  Young: 2,
  Adult: 3,
  Senior: 4,
  Unknown: 5,
};

const maxSize = 3 * 1024 * 1024;

function SortableItem({ id, value, onDelete, itemIndex, disabled }) {
  const { attributes, listeners, setNodeRef, transform, transition } = useSortable({ id, disabled });
  const style = { transform: CSS.Transform.toString(transform), transition };
  return (
    <div
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...listeners}
      className="relative flex-shrink-0 w-44 h-36 rounded-xl overflow-hidden"
    >
      <img
        src={value}
        alt="campaign"
        className="w-full h-full object-cover"
        style={{ cursor: disabled ? "default" : "grab" }}
      />
      {!disabled && (
        <button
          type="button"
          onPointerDown={(e) => e.stopPropagation()}
          onClick={(e) => { e.stopPropagation(); onDelete(itemIndex); }}
          className="absolute top-2 right-2 bg-white/80 hover:bg-white rounded-full p-1 text-red-500"
        >
          <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
            <path d="M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6v12zM19 4h-3.5l-1-1h-5l-1 1H5v2h14V4z" />
          </svg>
        </button>
      )}
    </div>
  );
}

function SortableList({ items, onSortEnd, onDelete, disabled, innerRef }) {
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));

  const handleDragEnd = ({ active, over }) => {
    if (over && active.id !== over.id) {
      const oldIndex = items.indexOf(active.id);
      const newIndex = items.indexOf(over.id);
      onSortEnd({ oldIndex, newIndex });
    }
  };

  return (
    <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
      <SortableContext items={items} strategy={horizontalListSortingStrategy}>
        <div
          ref={innerRef}
          className="flex flex-row gap-3 overflow-x-auto pb-2"
          style={{ scrollbarWidth: "none", msOverflowStyle: "none" }}
        >
          {items.map((value, index) => (
            <SortableItem
              key={value}
              id={value}
              value={value}
              itemIndex={index}
              onDelete={onDelete}
              disabled={disabled}
            />
          ))}
        </div>
      </SortableContext>
    </DndContext>
  );
}

export default function EditAnimalCampaignForm({
  campaignId,
  campaignInfo,
  onClose,
}) {
  const [campaignData, setCampaignData] = useState(() => {
    const imageUrls =
      campaignInfo?.campaign_images?.map((image) => image.image_url) || [];
    return {
      ...campaignInfo,
      image_urls: imageUrls,
    };
  });
  const [formEnabled, setFormEnabled] = useState(true);
  const [errorMsg, setErrorMsg] = useState("");
  const [updateCampaignSuccess, setCampaignSuccess] = useState(false);
  const [totalImages, setTotalImages] = useState({
    images: 0,
    graphic: 0,
  });
  const [deletedImageIds, setDeletedImageIds] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const imageGalleryRef = useRef(null);

  /* Handles Campaign Edit Update */
  const handleChange = (e) => {
    const { name, value } = e.target;

    // Prevent negative donation goals
    if (name === "donation_goal" && parseFloat(value) < 0) {
      return;
    }

    setCampaignData((prevState) => ({
      ...prevState,
      [name]: name === "age" ? ageOptions[value] : value, // Convert age to integer
    }));
  };

  const submitCampaignUpdate = async (e) => {
    e.preventDefault();
    setCampaignSuccess(false);
    setErrorMsg("");

    // Enforces at least 4 images (or 3 normal + 1 graphic)
    const hasEnoughImages = totalImages.images >= 4 ||
      (totalImages.images >= 3 && totalImages.graphic >= 1);

    if (!hasEnoughImages) {
      setErrorMsg(
        "Please upload at least 4 normal images or 3 normal with 1 graphic image."
      );
      return;
    }

    setIsSubmitting(true);

    try {
      let formData = new FormData();

      // Fields that should not be sent to the backend (read-only or computed)
      const excludedFields = [
        'last_archived', 'archived', 'created_at', 'id', 'nonprofit_name',
        'nonprofit_address', 'donations_details', 'update_info', 'image_urls',
        'updates', 'description', 'campaign_images', 'milestones_reached', 'error'
      ];

      Object.keys(campaignData).forEach((key) => {
        if (campaignData[key] !== "" && !excludedFields.includes(key)) {
          formData.append(key, campaignData[key]);
        }
      });

      // Add description as JSON string
      if (campaignData.description) {
        formData.append("description", JSON.stringify(campaignData.description));
      }

      // Add milestones_reached as JSON string (only once, outside loop)
      if (campaignData.milestones_reached) {
        formData.append("milestones_reached", JSON.stringify(campaignData.milestones_reached));
      }

      // sorts campaign_images based on image_urls since image_urls changes order when moved
      const campaignImages = [...campaignData.campaign_images];
      const imageUrls = [...campaignData.image_urls];

      // campaignImages gets sorted based on image_urls
      // this gets sent to the backend as "images"
      campaignImages.sort(function (a, b) {
        return imageUrls.indexOf(a.image_url) - imageUrls.indexOf(b.image_url);
      });

      campaignImages.forEach((image, index) => {
        if (image.file) {
          // if image hasn't been uploaded yet, it should contain a File
          if (image.graphic) {
            formData.append("graphic_images", image.file);
          } else {
            formData.append("new_images", image.file);
            formData.append("new_image_orders", index);
          }
        } else {
          // else, sending image ID and order to the backend to change it
          formData.append("existing_image_ids", image.id);
          formData.append("existing_image_orders", index);
        }
      });

      // Send deleted image IDs to the backend
      deletedImageIds.forEach(id => {
        formData.append("deleted_image_ids", id);
      });

      const response = await updateCampaign(campaignId, formData);
      if (response.success && response.data.animal_name) {
        setCampaignSuccess(true);
        setErrorMsg("");
        setDeletedImageIds([]); // Clear deleted IDs after successful save
      }
    } catch (error) {
      const errorMessage = errorHandler(error);
      setCampaignSuccess(false);
      setErrorMsg(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEditorChange = (content, delta, source, editor) => {
    setCampaignData((prevState) => ({
      ...prevState,
      description: content, // Set description as HTML content
    }));
  };

  const handleImageChange = async (event) => {
    let files = Array.from(event.target.files);
    let error = "";
    let isTooLarge = false;

    const validFiles = files.filter((file) => {
      if (file.size > maxSize) {
        error = "File size is too large (Must be under 3 MB).";
        isTooLarge = true;
        return false;
      }
      return true;
    });

    if (isTooLarge) {
      setErrorMsg(error);
      event.target.value = "";
      return;
    }

    try {
      const compressedFiles = await Promise.all(
        validFiles.map(async (file) => {
          return new Promise((resolve) => {
            new Compressor(file, {
              quality: 0.8,
              success: (compressedResult) => {
                resolve(new File([compressedResult], file.name, { type: compressedResult.type }));
              },
              error: () => resolve(file),
            });
          });
        })
      );

      setErrorMsg("");

      const newImages = compressedFiles.map((file) => ({
        file,
        url: URL.createObjectURL(file),
      }));

      setTotalImages((prevState) => ({
        ...prevState,
        images: prevState.images + compressedFiles.length,
      }));

      // Saves campaign_images with order field and image_url for deletion tracking
      setCampaignData((prevState) => {
        const startingIndex = prevState.campaign_images.length;
        const newCampaignImages = newImages.map((img, index) => ({
          file: img.file,
          image_url: img.url,
          order: startingIndex + index,
        }));
        return {
          ...prevState,
          campaign_images: [...prevState.campaign_images, ...newCampaignImages],
          image_urls: [
            ...prevState.image_urls,
            ...newImages.map((img) => img.url),
          ],
        };
      });
    } catch (error) {
      console.error("Error during file compression:", error);
    }
    event.target.value = "";
  };

  const handleGraphicImageChange = async (event) => {
    let files = Array.from(event.target.files);
    let error = "";
    let isTooLarge = false;

    const validFiles = files.filter((file) => {
      if (file.size > maxSize) {
        error = "File size is too large (Must be under 3 MB).";
        isTooLarge = true;
        return false;
      }
      return true;
    });

    if (isTooLarge) {
      setErrorMsg(error);
      event.target.value = "";
      return;
    }

    try {
      const compressedFiles = await Promise.all(
        validFiles.map(async (file) => {
          return new Promise((resolve) => {
            new Compressor(file, {
              quality: 0.8,
              success: (compressedResult) => {
                resolve(new File([compressedResult], file.name, { type: compressedResult.type }));
              },
              error: () => resolve(file),
            });
          });
        })
      );

      setErrorMsg("");

      setTotalImages((prevState) => ({
        ...prevState,
        graphic: prevState.graphic + compressedFiles.length,
      }));

      const newGraphicImages = compressedFiles.map((file) => ({
        file,
        graphic: true,
        url: URL.createObjectURL(file),
      }));

      // Saves campaign_images with order field and image_url for deletion tracking
      setCampaignData((prevState) => {
        const startingIndex = prevState.campaign_images.length;
        const newCampaignGraphicImages = newGraphicImages.map((img, index) => ({
          file: img.file,
          image_url: img.url,
          graphic: true,
          order: startingIndex + index,
        }));

        return {
          ...prevState,
          campaign_images: [
            ...prevState.campaign_images,
            ...newCampaignGraphicImages,
          ],
          image_urls: [
            ...prevState.image_urls,
            ...newGraphicImages.map((img) => img.url),
          ],
        };
      });
    } catch (error) {
      console.error("Error during file compression:", error);
    }
    event.target.value = "";
  };

  // Initialize totalImages count from existing campaign images
  useEffect(() => {
    let imageCount = 0;
    let graphicCount = 0;

    campaignData.campaign_images.forEach((img) => {
      if (img.graphic === true) {
        graphicCount += 1;
      } else {
        imageCount += 1;
      }
    });

    setTotalImages({
      images: imageCount,
      graphic: graphicCount,
    });
  }, []);

  useEffect(() => {
    // sort image_urls based on campaign_images on first render
    // changes everytime campaign_images change
    const campaignImages = [...campaignData.campaign_images];
    const imageUrls = [...campaignData.image_urls];

    // imageUrls gets sorted by campaignImages "order" key
    const mappingCampaignImages = {};
    campaignImages.forEach((img) => {
      mappingCampaignImages[img["image_url"]] = img["order"];
    });

    imageUrls.sort((a, b) => {
      return mappingCampaignImages[a] - mappingCampaignImages[b];
    });

    // this changes the order of campaign_images when rendering in the sortable list
    setCampaignData((prevState) => ({
      ...prevState,
      image_urls: imageUrls,
    }));
  }, [campaignData.campaign_images]);

  const onSortEnd = ({ oldIndex, newIndex }) => {
    setCampaignData((prevState) => ({
      ...prevState,
      image_urls: arrayMove(prevState.image_urls, oldIndex, newIndex),
    }));
  };

  const handleDeleteImage = (index) => {
    const imageUrl = campaignData.image_urls[index];
    const imageToDelete = campaignData.campaign_images.find(img => img.image_url === imageUrl);

    // Track ID if it's an existing image (has id)
    if (imageToDelete?.id) {
      setDeletedImageIds(prev => [...prev, imageToDelete.id]);
    }

    // Remove from state
    setCampaignData(prev => ({
      ...prev,
      image_urls: prev.image_urls.filter((_, i) => i !== index),
      campaign_images: prev.campaign_images.filter(img => img.image_url !== imageUrl)
    }));

    // Update image counts
    if (imageToDelete?.graphic) {
      setTotalImages(prev => ({ ...prev, graphic: prev.graphic - 1 }));
    } else {
      setTotalImages(prev => ({ ...prev, images: prev.images - 1 }));
    }
  };

  return (
    <>
      {/* update form  */}
      <CampaignUpdateForm campaignId={campaignId} campaignData={campaignData} />

      {/* Edit Campaign here */}
      <form onSubmit={submitCampaignUpdate} className="flex flex-col gap-6">
          {/* Image Upload/Rearrange */}
          <div className="flex flex-col gap-4">
            <p className="!font-bold !text-[16px] text-gray-900">Upload New Image/Graphic</p>

            {/* Combined drop zone */}
            <label className={`flex flex-col items-center justify-center gap-3 border-2 border-dashed border-gray-300 rounded-xl py-10 px-6 bg-gray-50 cursor-pointer hover:border-blue transition-colors ${!formEnabled ? "opacity-50 pointer-events-none" : ""}`}>
              <svg className="w-10 h-10 text-blue" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
              </svg>
              <p className="!text-[16px] text-gray-900">
                Drag & drop files or <span className="text-blue underline">choose file</span> to upload
              </p>
              <p className="!text-[13px] text-gray-400">Supported formates: JPG, JPEG, PNG (3MB)</p>
              <input
                type="file"
                name="images"
                multiple
                onChange={handleImageChange}
                disabled={!formEnabled}
                accept="image/jpeg, image/png, image/jpg"
                className="hidden"
              />
            </label>

            {/* Graphic image hidden upload (still supported) */}
            <input
              type="file"
              id="graphic-upload"
              name="graphic_images"
              multiple
              onChange={handleGraphicImageChange}
              disabled={!formEnabled}
              accept="image/jpeg, image/png, image/jpg"
              className="hidden"
            />

            {/* Gallery with navigation */}
            {(totalImages.images > 0 || totalImages.graphic > 0) && (
              <div>
                <div className="flex items-center justify-between mb-3">
                  <p className="!font-bold !text-[16px] text-gray-900">Click and drag to reorder</p>
                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={() => imageGalleryRef.current?.scrollBy({ left: -200, behavior: "smooth" })}
                      className="w-8 h-8 rounded-full border border-gray-300 flex items-center justify-center text-gray-600 hover:bg-gray-50"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                      </svg>
                    </button>
                    <button
                      type="button"
                      onClick={() => imageGalleryRef.current?.scrollBy({ left: 200, behavior: "smooth" })}
                      className="w-8 h-8 rounded-full border border-gray-300 flex items-center justify-center text-gray-600 hover:bg-gray-50"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                      </svg>
                    </button>
                  </div>
                </div>
                <SortableList
                  items={campaignData.image_urls}
                  onSortEnd={onSortEnd}
                  onDelete={handleDeleteImage}
                  disabled={!formEnabled}
                  innerRef={imageGalleryRef}
                  axis="x"
                  helperClass={editClasses.sortable_helper}
                  shouldCancelStart={(e) => e.target.closest('[data-no-drag="true"]') !== null}
                />
              </div>
            )}
          </div>

          {/* Full-width fields */}
          <div className="flex flex-col gap-5">
            <div>
              <label className="!font-bold !text-[14px] text-gray-900 block mb-1">Campaign Name</label>
              <input
                name="campaign_title"
                value={campaignData.campaign_title}
                onChange={handleChange}
                placeholder="E.g. Help Jane Doe"
                disabled={!formEnabled}
                className="w-full rounded-full border border-gray-300 px-4 py-2.5 text-sm focus:outline-none focus:border-blue disabled:bg-gray-50"
              />
            </div>

            <div>
              <label className="!font-bold !text-[14px] text-gray-900 block mb-1">Description</label>
              <textarea
                placeholder="Add your text here..."
                disabled={!formEnabled}
                className="w-full rounded-xl border border-gray-300 px-4 py-3 text-sm focus:outline-none focus:border-blue disabled:bg-gray-50 min-h-[120px] resize-none"
                defaultValue={typeof campaignData.description === "string" ? campaignData.description.replace(/<[^>]+>/g, "") : ""}
                onChange={(e) => setCampaignData(prev => ({ ...prev, description: e.target.value }))}
              />
            </div>
          </div>

          {/* Two-column layout: form fields (left) + goal/share sidebar (right) */}
          <div className="grid grid-cols-2 gap-8 items-stretch">
            {/* Left: form fields */}
            <div className="flex flex-col gap-5">
              <div>
                <label className="!font-bold !text-[14px] text-gray-900 block mb-1">Donation Goal</label>
                <input
                  type="number"
                  name="donation_goal"
                  value={campaignData.donation_goal}
                  onChange={handleChange}
                  placeholder="E.g. 10"
                  disabled={!formEnabled}
                  className="w-full rounded-full border border-gray-300 px-4 py-2.5 text-sm focus:outline-none focus:border-blue disabled:bg-gray-50"
                />
              </div>

              <div>
                <label className="!font-bold !text-[14px] text-gray-900 block mb-1">Donation Box Title</label>
                <input
                  name="donation_box_title"
                  value={campaignData.donation_box_title}
                  onChange={handleChange}
                  placeholder="E.g. Donation"
                  maxLength="30"
                  disabled={!formEnabled}
                  className="w-full rounded-full border border-gray-300 px-4 py-2.5 text-sm focus:outline-none focus:border-blue disabled:bg-gray-50"
                />
              </div>

              <div>
                <label className="!font-bold !text-[14px] text-gray-900 block mb-1">Video URL</label>
                <input
                  name="video_urls"
                  value={campaignData.video_urls}
                  onChange={handleChange}
                  placeholder="Video URLs (separated with comma)"
                  disabled={!formEnabled}
                  className="w-full rounded-full border border-gray-300 px-4 py-2.5 text-sm focus:outline-none focus:border-blue disabled:bg-gray-50"
                />
              </div>

              <div>
                <label className="!font-bold !text-[14px] text-gray-900 block mb-1">Animal Name</label>
                <input
                  name="animal_name"
                  value={campaignData.animal_name}
                  onChange={handleChange}
                  placeholder="E.g. Jane Doe"
                  disabled={!formEnabled}
                  className="w-full rounded-full border border-gray-300 px-4 py-2.5 text-sm focus:outline-none focus:border-blue disabled:bg-gray-50"
                />
              </div>

              <div>
                <label className="!font-bold !text-[14px] text-gray-900 block mb-1">Age</label>
                <div className="relative">
                  <select
                    name="age"
                    value={Object.keys(ageOptions).find(key => ageOptions[key] === campaignData.age) || ""}
                    onChange={handleChange}
                    disabled={!formEnabled}
                    className="w-full rounded-full border border-gray-300 px-4 py-2.5 text-sm appearance-none focus:outline-none focus:border-blue disabled:bg-gray-50 bg-white"
                  >
                    <option value="" disabled>Select Age</option>
                    {Object.keys(ageOptions).map(age => <option key={age} value={age}>{age}</option>)}
                  </select>
                  <svg className="absolute right-4 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </div>
              </div>

              <div>
                <label className="!font-bold !text-[14px] text-gray-900 block mb-1">Color</label>
                <input
                  name="color"
                  value={campaignData.color}
                  onChange={handleChange}
                  placeholder="E.g. Brown"
                  disabled={!formEnabled}
                  className="w-full rounded-full border border-gray-300 px-4 py-2.5 text-sm focus:outline-none focus:border-blue disabled:bg-gray-50"
                />
              </div>

              <div>
                <label className="!font-bold !text-[14px] text-gray-900 block mb-1">Breed</label>
                <input
                  name="breed"
                  value={campaignData.breed}
                  onChange={handleChange}
                  placeholder="E.g. Dog"
                  disabled={!formEnabled}
                  className="w-full rounded-full border border-gray-300 px-4 py-2.5 text-sm focus:outline-none focus:border-blue disabled:bg-gray-50"
                />
              </div>

              <div>
                <label className="!font-bold !text-[14px] text-gray-900 block mb-1">Gender</label>
                <div className="relative">
                  <select
                    name="gender"
                    value={campaignData.gender}
                    onChange={handleChange}
                    disabled={!formEnabled}
                    className="w-full rounded-full border border-gray-300 px-4 py-2.5 text-sm appearance-none focus:outline-none focus:border-blue disabled:bg-gray-50 bg-white"
                  >
                    <option value="" disabled>Select Gender</option>
                    <option value="female">Female</option>
                    <option value="male">Male</option>
                  </select>
                  <svg className="absolute right-4 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </div>
              </div>

              <div>
                <label className="!font-bold !text-[14px] text-gray-900 block mb-1">Species</label>
                <div className="relative">
                  <select
                    name="species"
                    value={campaignData.species}
                    onChange={handleChange}
                    disabled={!formEnabled}
                    className="w-full rounded-full border border-gray-300 px-4 py-2.5 text-sm appearance-none focus:outline-none focus:border-blue disabled:bg-gray-50 bg-white"
                  >
                    <option value="" disabled>Select Species</option>
                    <option value="dog">Dog</option>
                    <option value="cat">Cat</option>
                    <option value="farm">Farm</option>
                    <option value="exotic">Exotic</option>
                    <option value="other">Other</option>
                  </select>
                  <svg className="absolute right-4 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </div>
              </div>

            </div>

            {/* Right: goal card + share card */}
            <div className="flex flex-col gap-4 h-full">
              {/* Goal card */}
              <div className="border border-gray-200 rounded-2xl p-5 flex flex-col gap-3">
                <h2 className="!font-bold !text-[22px] text-gray-900">
                  ${parseFloat(campaignInfo.donation_goal || 0).toFixed(2)} Goal
                </h2>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div
                    className="bg-blue h-2 rounded-full"
                    style={{ width: `${Math.min(campaignInfo.donation_goal > 0 ? (campaignInfo.donation_amount / campaignInfo.donation_goal) * 100 : 0, 100)}%` }}
                  />
                </div>
                <div className="flex justify-between text-sm">
                  <div>
                    <p className="text-gray-500 text-xs">Goal</p>
                    <p className="!font-semibold">${campaignInfo.donation_goal}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-gray-500 text-xs">Raised</p>
                    <p className="!font-semibold">${campaignInfo.donation_amount}</p>
                  </div>
                </div>
              </div>

              {/* Share card */}
              <div className="flex-1 [&>div]:!w-full [&>div]:!h-full [&>div]:!min-h-0">
                <CampaignShareBox campaignData={campaignInfo} />
              </div>
            </div>
          </div>

          {/* Messages */}
          {updateCampaignSuccess && <p className="text-green-500 text-sm">Campaign has been edited successfully!</p>}
          {!updateCampaignSuccess && errorMsg.length > 0 && <p className="text-red-500 text-sm">{errorMsg}</p>}

          {/* Action buttons */}
          <div className="flex gap-4">
            <button
              type="button"
              onClick={() => {
                const path = campaignInfo.campaign_type === "animal"
                  ? `/campaigns/animal/${urlifyName(campaignInfo.nonprofit_name)}/${urlifyName(campaignInfo.animal_name)}/${campaignInfo.id}`
                  : `/campaigns/${campaignInfo.campaign_type}/${urlifyName(campaignInfo.nonprofit_name)}/${urlifyName(campaignInfo.campaign_title)}/${campaignInfo.id}`;
                window.open(`${window.location.origin}${path}`, "_blank");
              }}
              className="flex-1 py-3 rounded-full bg-blue text-white !font-bold text-sm"
            >
              Preview Campaign
            </button>
          </div>
          <button
            type="submit"
            disabled={isSubmitting}
            className="w-full py-3 rounded-full bg-blue-navy text-white !font-bold text-sm disabled:opacity-50"
          >
            {isSubmitting ? "Saving..." : "Save"}
          </button>
        </form>
    </>
  );
}
